import { useMutation, useQueryClient, useIsMutating } from '@tanstack/react-query';
import { DashboardComponentDto, DashboardComponentModel } from 'src/models/account';
import { DashboardObjectDto } from '../../../../../models/account';
import { useSettings } from 'src/@core/hooks/useSettings';
import {
  deleteDashboardComponentById,
  postDashboardComponent,
  updateDashboardComponentById
} from 'src/services/account';

export const useDashboardMutations = () => {
  const queryClient = useQueryClient();
  const { settings } = useSettings();
  const isMutating = useIsMutating();

  /**
   * Handle saving/updating of dashboard components
   *
   * @param newDashboard DashboardObjectDto
   * @param dashboardQueryKey any
   * @param dashboardId string
   * @param refetchDashboard () => void
   * @returns void
   */
  const saveDashboard = (
    newDashboard: DashboardObjectDto,
    dashboardQueryKey: any,
    dashboardId: string,
    refetchDashboard: () => void,
    onSuccess?: () => void | undefined
  ) => {
    return new Promise((resolve, reject) => {
      if (
        updateDashboardComponentMutation.status === 'loading' ||
        saveNewDashboardComponentMutation.status === 'loading' ||
        isMutating
      ) {
        reject("Can't save dashboard while saving/updating dashboard components");
      }

      const promises = newDashboard.dashboardComponents?.map((component: any) => {
        /**
         * If component property isDeleted is true, delete it
         */
        if (component.hasOwnProperty('isDeleted') && component.isDeleted) {
          return deleteDashboardComponentMutation.mutateAsync({ id: component.i, component });
        }

        /**
         * If component has version, update it
         */
        if (component.hasOwnProperty('version')) {
          const updatedComponent = {
            version: component.version,
            dashboardId: dashboardId,
            dashboardWidgetId: component.dashboardWidgetId,
            widgetXposition: component.x,
            widgetYposition: component.y,
            widgetWidth: component.w,
            widgetHeight: component.h,
            isStatic: component.static
          };

          return updateDashboardComponentMutation.mutateAsync(
            { id: component.i, component: updatedComponent },
            {
              onSuccess: ({ data: { data } }) => {
                if (!data || !newDashboard.dashboardComponents) return;

                const index = newDashboard.dashboardComponents?.findIndex(
                  (existingComponent: any) => existingComponent.i === component.i
                );

                (newDashboard.dashboardComponents[index] as any) = {
                  ...newDashboard.dashboardComponents[index],
                  version: data.version
                };
              }
            }
          );
        }

        /**
         * If component does not have version, save it as new component
         */
        const newComponent = {
          dashboardId: dashboardId,
          dashboardWidgetId: component.dashboardWidgetId,
          widgetXposition: component.x,
          widgetYposition: component.y,
          widgetWidth: component.w,
          widgetHeight: component.h,
          isStatic: component.static
        };

        return saveNewDashboardComponentMutation.mutateAsync(newComponent, {
          onSuccess: ({ data: { data } }) => {
            if (!data || !newDashboard.dashboardComponents) return;

            const index = newDashboard.dashboardComponents?.findIndex(
              (existingComponent: any) => existingComponent.i === component.i
            );

            (newDashboard.dashboardComponents[index] as any) = {
              ...newDashboard.dashboardComponents[index],
              i: data.id,
              version: data.version
            };
          }
        });
      });

      Promise.all(promises as Promise<any>[])
        .then(() => {
          refetchDashboard();
          resolve(true);
          onSuccess && onSuccess();
        })
        .catch(error => {
          console.log('Save dashboard error', error.message);
          reject(error.message);
        });
    });
  };

  const deleteDashboardComponent = (
    component: any,
    newDashboard: DashboardObjectDto,
    dashboardQueryKey: any,
    refetchDashboard: () => void
  ) => {
    queryClient.setQueryData(dashboardQueryKey, newDashboard);

    /**
     * If dashboard is locked, do nothing
     */
    if (!settings.unlockDashboardLayout) return;

    if (component.hasOwnProperty('version')) {
      return deleteDashboardComponentMutation.mutateAsync(
        { id: component.i, component },
        {
          onSuccess: () => {
            refetchDashboard();
            deleteDashboardComponentMutation.reset();
          },
          onError: (error: any) => {
            console.log(error.message);
            deleteDashboardComponentMutation.reset();
          }
        }
      );
    }
  };

  /**
   * Save dashboard component
   */
  const saveNewDashboardComponentMutation = useMutation((component: DashboardComponentModel) => {
    return postDashboardComponent({ data: component });
  });

  /**
   * Update dashboard component
   */
  const updateDashboardComponentMutation = useMutation(
    ({ id, component }: { id: string; component: DashboardComponentDto }) => {
      return updateDashboardComponentById(id, { data: component });
    }
  );

  /**
   * Delete dashboard component
   */
  const deleteDashboardComponentMutation = useMutation(
    ({ id, component }: { id: string; component: DashboardComponentDto }) => {
      const reqBody = {
        version: component?.version ?? '',
        remarks: ''
      };

      return deleteDashboardComponentById(id, reqBody);
    }
  );

  return {
    saveDashboard,
    deleteDashboardComponent
  };
};
